import { useCallback } from "react"
import { useMutation } from "urql"

import {
  type CustomerPurchaseOrderDeleteFailure,
  type CustomerPurchaseOrderDeleteInput,
  DeleteCustomerPurchaseOrderDocument,
  type DeleteCustomerPurchaseOrderMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { PERMISSION_DENIED_MUTATION_MESSAGE, isForbiddenError } from "@/utils/urql/permissionError"

interface UseDeleteCustomerPurchaseOrderReturn {
  deleteCustomerPurchaseOrder: (customerPurchaseOrderId: string) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteCustomerPurchaseOrder = (): UseDeleteCustomerPurchaseOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    DeleteCustomerPurchaseOrderMutation,
    { input: CustomerPurchaseOrderDeleteInput }
  >(DeleteCustomerPurchaseOrderDocument)

  const deleteCustomerPurchaseOrder = useCallback(
    async (customerPurchaseOrderId: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { customerPurchaseOrderId } })
      const output = data?.customerPurchaseOrder.delete
      const hasError = error || output?.__typename === "CustomerPurchaseOrderDeleteFailure"

      if (hasError) {
        const message = isForbiddenError(error)
          ? PERMISSION_DENIED_MUTATION_MESSAGE
          : (error?.message ?? (output as CustomerPurchaseOrderDeleteFailure).error.message)
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Customer purchase order deleted" })
      return true
    },
    [executeMutation, notify]
  )

  return { deleteCustomerPurchaseOrder, isDeleting: fetching }
}
